import React, {
  useState,
  useRef,
  useEffect,
  createContext,
  useContext
} from 'react';
import type { ValidIconName } from 'ehs-react';
import { FocusTrap } from 'ehs-react';
import { Button } from 'ehs-react';

interface PopupMenuProps {
  children: React.ReactNode;
  icon: ValidIconName;
  arialLabel?: string;
  className?: string;
  dataTestId?: string;
}

interface PopupMenuContextType {
  close: () => void;
}

const PopupMenuContext = createContext<PopupMenuContextType | undefined>(
  undefined
);

export const usePopupMenu = (): PopupMenuContextType => {
  const context = useContext(PopupMenuContext);
  if (!context) {
    console.error('usePopupMenu must be used within a PopupMenu');
    return {
      close: () => console.error('no popup menu context')
    };
  }
  return context;
};

export const PopupMenu: React.FC<PopupMenuProps> = ({
  children,
  icon,
  arialLabel,
  className,
  dataTestId
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<
    'bottom-left' | 'top-left' | 'bottom-right' | 'top-right'
  >('bottom-left');
  const buttonRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && buttonRef.current && popupRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const popupRect = popupRef.current.getBoundingClientRect();
      const viewHeight = window.innerHeight;
      const viewWidth = window.innerWidth;

      let newPosition: typeof position = 'bottom-left';
      if (buttonRect.bottom + popupRect.height > viewHeight) {
        newPosition = 'top-left';
      }
      if (buttonRect.left + popupRect.width > viewWidth) {
        newPosition =
          newPosition === 'bottom-left' ? 'bottom-right' : 'top-right';
      }

      setPosition(newPosition);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const close = () => setIsOpen(false);

  return (
    <div ref={buttonRef} className={`popup-widget ${className}`}>
      <FocusTrap active={isOpen}>
        <div>
          <div>
            <Button
              className="popup-button"
              icon={icon}
              variant="gray"
              text={arialLabel ?? ''}
              onClick={() => setIsOpen(isOpen => !isOpen)}
              data-testid={dataTestId}
            />
          </div>
          {isOpen && (
            <PopupMenuContext.Provider value={{ close }}>
              <div
                ref={popupRef}
                className={`popup-content ${position}`}
                role="dialog"
                aria-modal="false"
              >
                {children}
              </div>
            </PopupMenuContext.Provider>
          )}
        </div>
      </FocusTrap>
    </div>
  );
};
